/** @jsxImportSource react */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert, Box, Button, Chip, IconButton, Link, MenuItem, Paper, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
  useMediaQuery,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import AddIcon from '@mui/icons-material/Add'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined'
import { useQuery } from '@tanstack/react-query'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
  addDays, addMonths, differenceInMinutes, endOfMonth, endOfWeek, format, isSameDay, isSameMonth, isToday, startOfDay,
  startOfMonth, startOfWeek,
} from 'date-fns'
import { calendarAPI, emailRepliesAPI } from '../api/crm'
import { useTenantKey } from '../hooks/useCrmQueries'
import { selectIsTenantAdmin } from '../store/slices/tenantSlice'
import { getErrorMessage } from '../utils/apiErrors'
import PageHeader from '../components/common/PageHeader'
import MeetingDialog from '../components/meetings/MeetingDialog'
import { assignable, useMembers } from '../components/records/PersonInput'

const HOUR = 48                       // px per hour in day / week views
const WEEK_OPTS = { weekStartsOn: 1 }
const local = (d) => format(d, "yyyy-MM-dd'T'HH:mm:ss")
const PEOPLE_COLORS = ['primary', 'secondary', 'success', 'warning', 'info', 'error']

/** The period shown: [from, to) and the days in it. */
const periodOf = (view, anchor) => {
  if (view === 'day') return { from: startOfDay(anchor), to: addDays(startOfDay(anchor), 1) }
  if (view === 'week') return { from: startOfWeek(anchor, WEEK_OPTS), to: addDays(startOfWeek(anchor, WEEK_OPTS), 7) }
  const from = startOfWeek(startOfMonth(anchor), WEEK_OPTS)
  return { from, to: addDays(endOfWeek(endOfMonth(anchor), WEEK_OPTS), 1) }
}
const daysOf = ({ from, to }) => {
  const out = []
  for (let d = from; d < to; d = addDays(d, 1)) out.push(d)
  return out
}

/** Side-by-side lanes for overlapping meetings in one day column. */
const layoutDay = (items) => {
  const sorted = [...items].sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt))
  const lanes = []
  const placed = sorted.map((m) => {
    const start = new Date(m.startsAt)
    let lane = lanes.findIndex((end) => end <= start)
    if (lane < 0) { lane = lanes.length; lanes.push(null) }
    lanes[lane] = new Date(m.endsAt)
    return { m, lane }
  })
  return placed.map((p) => ({ ...p, lanes: Math.max(1, lanes.length) }))
}

const CalendarPage = () => {
  const theme = useTheme()
  const phone = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()
  const tenantId = useTenantKey()
  const isAdmin = useSelector(selectIsTenantAdmin)
  const { data: members = [] } = useMembers()
  const [view, setView] = useState(phone ? 'day' : 'week')
  const [anchor, setAnchor] = useState(new Date())
  const [whose, setWhose] = useState('me')      // me | team | <userId>
  const [dialog, setDialog] = useState(null)    // { start } | meeting
  const period = periodOf(view, anchor)
  const days = daysOf(period)
  const params = { from: local(period.from), to: local(period.to), ...(whose === 'team' ? { team: true } : whose !== 'me' ? { userId: whose } : {}) }
  const { data, isLoading, error } = useQuery({
    queryKey: [tenantId, 'calendar', params],
    queryFn: () => calendarAPI.get(params),
    placeholderData: (prev) => prev,
  })
  const meetings = data?.meetings || []
  const tasks = data?.tasks || []
  // other events in people's own Google / Outlook calendars (titles only in yours)
  const external = data?.external || []
  const myCalendar = whose === 'me' ? (data?.calendars || [])[0] : null
  const calendarErrors = (data?.calendars || []).filter((c) => c.error)
  const mailboxes = useQuery({
    queryKey: [tenantId, 'email-replies', 'mailboxes'],
    queryFn: emailRepliesAPI.mailboxes,
    enabled: !!myCalendar && !myCalendar.connected,
    staleTime: 300_000,
  })
  const canConnect = (mailboxes.data?.available || []).length > 0
  const colorOf = useMemo(() => {
    const ids = (data?.people || []).map((p) => p.id)
    return (userId) => theme.palette[PEOPLE_COLORS[Math.max(0, ids.indexOf(userId)) % PEOPLE_COLORS.length]].main
  }, [data, theme])

  const scroller = useRef(null)
  // open at 08:00, once the grid is there (it isn't while the first load runs)
  const gridShown = !!data && view !== 'month'
  useEffect(() => {
    if (scroller.current && gridShown) scroller.current.scrollTop = 8 * HOUR - 8
  }, [view, gridShown])

  const step = (dir) => setAnchor((a) => (view === 'day' ? addDays(a, dir) : view === 'week' ? addDays(a, 7 * dir) : addMonths(a, dir)))
  const title = view === 'month' ? format(anchor, 'MMMM yyyy')
    : view === 'day' ? format(anchor, 'EEEE d MMMM yyyy')
      : `${format(period.from, 'd MMM')} – ${format(addDays(period.to, -1), isSameMonth(period.from, addDays(period.to, -1)) ? 'd MMM yyyy' : 'd MMM yyyy')}`
  const team = whose === 'team'

  const MeetingBlock = ({ m, style }) => {
    const color = team ? colorOf(m.owner.id) : theme.palette.primary.main
    const short = differenceInMinutes(new Date(m.endsAt), new Date(m.startsAt)) < 45
    return (
      <Tooltip title={`${format(new Date(m.startsAt), 'HH:mm')}–${format(new Date(m.endsAt), 'HH:mm')} · ${m.title}${m.record ? ` · ${m.record.title}` : ''}${team ? ` · ${m.owner.title}` : ''}`}>
        <Box onClick={(e) => { e.stopPropagation(); setDialog(m) }}
          sx={{ position: 'absolute', overflow: 'hidden', cursor: 'pointer', borderRadius: 1, px: 0.75, py: short ? 0 : 0.25,
            bgcolor: alpha(color, m.status === 'PLANNED' ? 0.16 : 0.07), borderLeft: 3, borderColor: color,
            opacity: m.status === 'CANCELLED' ? 0.55 : 1, '&:hover': { bgcolor: alpha(color, 0.26) }, ...style }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', lineHeight: 1.3, color: 'text.primary',
            textDecoration: m.status === 'CANCELLED' ? 'line-through' : 'none', whiteSpace: short ? 'nowrap' : 'normal' }}>
            {m.kind === 'CALL' && <PhoneOutlinedIcon sx={{ fontSize: 12, mr: 0.25, verticalAlign: -2 }} />}{m.title}
          </Typography>
          {!short && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
            {format(new Date(m.startsAt), 'HH:mm')}{m.record ? ` · ${m.record.title}` : ''}
          </Typography>}
        </Box>
      </Tooltip>
    )
  }

  /** An event from someone's own calendar: grey, behind the CRM's meetings; clicks go through to plan a meeting. */
  const ExternalBlock = ({ e, style }) => {
    const short = differenceInMinutes(new Date(e.end), new Date(e.start)) < 45
    const label = e.title || 'Busy'
    return (
      <Box sx={{ position: 'absolute', overflow: 'hidden', pointerEvents: 'none', borderRadius: 1, px: 0.75, py: short ? 0 : 0.25,
        bgcolor: alpha(theme.palette.text.primary, 0.06), borderLeft: 3, borderColor: team ? colorOf(e.userId) : theme.palette.text.disabled,
        backgroundImage: `repeating-linear-gradient(135deg, transparent 0 6px, ${alpha(theme.palette.text.primary, 0.04)} 6px 12px)`, ...style }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3, whiteSpace: short ? 'nowrap' : 'normal' }}>
          {label}{!short && ` · ${format(new Date(e.start), 'HH:mm')}`}
        </Typography>
      </Box>
    )
  }

  const TaskChip = ({ t }) => (
    <Chip size="small" icon={<TaskAltOutlinedIcon />} label={`${format(new Date(t.dueAt), 'HH:mm')} ${t.title}`} variant="outlined"
      onClick={() => (t.record ? navigate(`/records/${t.record.entityType}/${t.record.id}`) : navigate('/tasks'))}
      sx={{ maxWidth: '100%', justifyContent: 'flex-start', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />
  )

  const timeGrid = (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      {/* day headers + tasks */}
      <Box sx={{ display: 'grid', gridTemplateColumns: `52px repeat(${days.length}, minmax(0, 1fr))`, borderBottom: 1, borderColor: 'divider' }}>
        <Box />
        {days.map((d) => {
          const dayTasks = tasks.filter((t) => isSameDay(new Date(t.dueAt), d))
          const allDay = external.filter((e) => e.allDay && new Date(e.start) <= d && new Date(e.end) > d)
          return (
            <Box key={d.toISOString()} sx={{ p: 0.75, borderLeft: 1, borderColor: 'divider', minWidth: 0 }}>
              <Typography variant="caption" color={isToday(d) ? 'primary' : 'text.secondary'} sx={{ fontWeight: isToday(d) ? 700 : 500 }}>
                {format(d, view === 'day' ? 'EEEE d' : 'EEE d')}
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {allDay.map((e, i) => (
                  <Chip key={`ad${i}`} size="small" label={`All day: ${e.title || 'Busy'}`}
                    sx={{ maxWidth: '100%', bgcolor: alpha(theme.palette.text.primary, 0.08), '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />
                ))}
                {dayTasks.slice(0, 3).map((t) => <TaskChip key={t.id} t={t} />)}
              </Stack>
              {dayTasks.length > 3 && <Typography variant="caption" color="text.secondary">+{dayTasks.length - 3} tasks</Typography>}
            </Box>
          )
        })}
      </Box>
      <Box ref={scroller} sx={{ height: { xs: 'calc(100vh - 300px)', md: 'calc(100vh - 290px)' }, minHeight: 360, overflowY: 'auto' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: `52px repeat(${days.length}, minmax(0, 1fr))`, position: 'relative' }}>
          <Box>
            {Array.from({ length: 24 }, (_, h) => (
              <Typography key={h} variant="caption" color="text.secondary" sx={{ display: 'block', height: HOUR, textAlign: 'right', pr: 1, transform: 'translateY(-8px)' }}>
                {h ? `${String(h).padStart(2, '0')}:00` : ''}
              </Typography>
            ))}
          </Box>
          {days.map((d) => {
            const dayMeetings = meetings.filter((m) => new Date(m.startsAt) < addDays(d, 1) && new Date(m.endsAt) > d)
            return (
              <Box key={d.toISOString()} sx={{ position: 'relative', borderLeft: 1, borderColor: 'divider', height: 24 * HOUR,
                bgcolor: isToday(d) ? alpha(theme.palette.primary.main, 0.03) : 'transparent' }}>
                {Array.from({ length: 24 }, (_, h) => (
                  <Box key={h} onClick={() => setDialog({ start: format(new Date(d.getFullYear(), d.getMonth(), d.getDate(), h), "yyyy-MM-dd'T'HH:mm") })}
                    sx={{ height: HOUR, borderTop: h ? 1 : 0, borderColor: 'divider', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} />
                ))}
                {isToday(d) && (
                  <Box sx={{ position: 'absolute', left: 0, right: 0, top: (differenceInMinutes(new Date(), d) / 60) * HOUR, borderTop: 2, borderColor: 'error.main', pointerEvents: 'none' }} />
                )}
                {external.filter((e) => !e.allDay && new Date(e.start) < addDays(d, 1) && new Date(e.end) > d).map((e, i) => {
                  const start = Math.max(0, differenceInMinutes(new Date(e.start), d))
                  const end = Math.min(24 * 60, differenceInMinutes(new Date(e.end), d))
                  return <ExternalBlock key={`x${i}`} e={e} style={{ top: (start / 60) * HOUR + 1, height: Math.max(18, ((end - start) / 60) * HOUR - 2),
                    left: 2, right: 2 }} />
                })}
                {layoutDay(dayMeetings).map(({ m, lane, lanes }) => {
                  const start = Math.max(0, differenceInMinutes(new Date(m.startsAt), d))
                  const end = Math.min(24 * 60, differenceInMinutes(new Date(m.endsAt), d))
                  return <MeetingBlock key={m.id} m={m} style={{ top: (start / 60) * HOUR + 1, height: Math.max(20, ((end - start) / 60) * HOUR - 2),
                    left: `calc(${(100 / lanes) * lane}% + 2px)`, width: `calc(${100 / lanes}% - 4px)` }} />
                })}
              </Box>
            )
          })}
        </Box>
      </Box>
    </Paper>
  )

  const monthGrid = (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
        {days.slice(0, 7).map((d) => (
          <Typography key={d.toISOString()} variant="caption" color="text.secondary" sx={{ p: 0.75, fontWeight: 600, borderBottom: 1, borderColor: 'divider' }}>
            {format(d, phone ? 'EEEEE' : 'EEE')}
          </Typography>
        ))}
        {days.map((d) => {
          const items = [
            ...meetings.filter((m) => isSameDay(new Date(m.startsAt), d)).map((m) => ({ at: m.startsAt, m })),
            ...tasks.filter((t) => isSameDay(new Date(t.dueAt), d)).map((t) => ({ at: t.dueAt, t })),
            ...external.filter((e) => (e.allDay ? new Date(e.start) <= d && new Date(e.end) > d : isSameDay(new Date(e.start), d)))
              .map((e, i) => ({ at: e.allDay ? d.toISOString() : e.start, x: { ...e, id: `x${i}${e.start}`, title: e.title || 'Busy' } })),
          ].sort((a, b) => new Date(a.at) - new Date(b.at))
          return (
            <Box key={d.toISOString()} onClick={() => { setAnchor(d); setView('day') }}
              sx={{ minHeight: { xs: 64, md: 108 }, p: 0.5, borderTop: 1, borderLeft: d.getDay() === 1 ? 0 : 1, borderColor: 'divider', cursor: 'pointer',
                minWidth: 0, bgcolor: isSameMonth(d, anchor) ? 'transparent' : 'action.hover', '&:hover': { bgcolor: 'action.selected' } }}>
              <Typography variant="caption" color={isToday(d) ? 'primary' : 'text.secondary'} sx={{ fontWeight: isToday(d) ? 700 : 400 }}>
                {format(d, 'd')}
              </Typography>
              {!phone && items.slice(0, 3).map((x) => (
                <Typography key={(x.m || x.t || x.x).id} variant="caption" noWrap sx={{ display: 'block', lineHeight: 1.4,
                  color: x.m ? 'text.primary' : 'text.secondary', textDecoration: x.m?.status === 'CANCELLED' ? 'line-through' : 'none' }}>
                  <Box component="span" sx={{ display: 'inline-block', width: 6, height: 6, borderRadius: x.x ? 0.5 : '50%', mr: 0.5, verticalAlign: 1,
                    bgcolor: x.m ? (team ? colorOf(x.m.owner.id) : 'primary.main') : x.x ? alpha(theme.palette.text.primary, 0.25) : 'text.disabled' }} />
                  {x.x?.allDay ? 'All day' : format(new Date(x.at), 'HH:mm')} {(x.m || x.t || x.x).title}
                </Typography>
              ))}
              {phone && items.length > 0 && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', mt: 0.5 }} />}
              {!phone && items.length > 3 && <Typography variant="caption" color="text.secondary">+{items.length - 3} more</Typography>}
            </Box>
          )
        })}
      </Box>
    </Paper>
  )

  return (
    <Box>
      <PageHeader title="Calendar" subtitle="Meetings, calls and dated tasks, with your own calendar’s other events in grey. Click a free time to plan a meeting."
        actions={<Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ start: null })}>Plan meeting</Button>} />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 1.5 }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Button size="small" variant="outlined" onClick={() => setAnchor(new Date())}>Today</Button>
          <IconButton size="small" aria-label="Previous" onClick={() => step(-1)}><ChevronLeftIcon /></IconButton>
          <IconButton size="small" aria-label="Next" onClick={() => step(1)}><ChevronRightIcon /></IconButton>
          <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 600 }}>{title}</Typography>
        </Stack>
        <Box sx={{ flexGrow: 1 }} />
        <TextField select size="small" value={whose} onChange={(e) => setWhose(e.target.value)} sx={{ minWidth: 200 }} label="Whose">
          <MenuItem value="me">My calendar</MenuItem>
          <MenuItem value="team">My team</MenuItem>
          {isAdmin && assignable(members).map((m) => <MenuItem key={m.userId} value={m.userId}>{m.name || m.email}</MenuItem>)}
        </TextField>
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
          <ToggleButton value="day">Day</ToggleButton>
          <ToggleButton value="week">Week</ToggleButton>
          <ToggleButton value="month">Month</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {team && (data?.people || []).length > 1 && (
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
          {data.people.map((p) => (
            <Chip key={p.id} size="small" label={p.title} variant="outlined"
              icon={<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colorOf(p.id), ml: '8px !important' }} />} />
          ))}
        </Stack>
      )}
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{getErrorMessage(error)}</Alert>}
      {myCalendar && !myCalendar.connected && canConnect && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          See your Google or Outlook events here and avoid double-booking: <Link component={RouterLink} to="/settings/email-replies">connect your calendar</Link>.
        </Alert>
      )}
      {calendarErrors.map((c) => <Alert key={c.userId} severity="warning" sx={{ mb: 1.5 }}>{c.error}</Alert>)}
      {isLoading && !data ? <Typography color="text.secondary">Loading…</Typography> : view === 'month' ? monthGrid : timeGrid}
      {dialog && (
        <MeetingDialog open onClose={() => setDialog(null)} meeting={dialog.id ? dialog : null} start={dialog.start || undefined} />
      )}
    </Box>
  )
}

export default CalendarPage
